import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, ListChecks, CheckCircle2, Clock, CalendarClock } from "lucide-react";
import { StatCard, Loading, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

function isToday(d) { if (!d) return false; return d === new Date().toISOString().slice(0, 10); }

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState(null);
  const [orders, setOrders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", order_id: "", assigned_staff_id: "", due_date: "" });

  const load = useCallback(async () => setTasks((await api.get("/tasks")).data), []);
  useEffect(() => {
    load();
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    if (isAdmin) api.get("/users", { params: { role: "staff" } }).then((r) => setStaff(r.data)).catch(() => {});
  }, [load, isAdmin]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try { await api.post("/tasks", { ...f, order_id: f.order_id || null, assigned_staff_id: f.assigned_staff_id || null }); toast.success("Task created"); setOpen(false); setF({ title: "", description: "", order_id: "", assigned_staff_id: "", due_date: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, null, { params: { status: t.status === "pending" ? "completed" : "pending" } }); load(); };

  if (tasks === null) return <Loading />;

  const pending = tasks.filter((t) => t.status === "pending");
  const dueToday = pending.filter((t) => isToday(t.due_date));
  const completed = tasks.filter((t) => t.status === "completed");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">{isAdmin ? "Tasks" : "My Tasks"}</h1>
          <p className="text-sm text-slate-500 mt-1">Track and complete order tasks.</p>
        </div>
        <Button data-testid="add-task-button" onClick={() => setOpen(true)} className="bg-[#1e3a8a] hover:bg-[#1e40af]"><Plus className="h-4 w-4 mr-1.5" /> New Task</Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={ListChecks} tone="blue" label="All Tasks" value={tasks.length} />
        <StatCard icon={Clock} tone="amber" label="Pending" value={pending.length} />
        <StatCard icon={CalendarClock} tone="rose" label="Due Today" value={dueToday.length} />
      </div>

      <div className="bg-white border rounded-xl shadow-sm">
        <div className="px-5 py-3.5 border-b"><h3 className="font-semibold font-display text-slate-900">Pending Tasks</h3></div>
        <div className="divide-y">
          {pending.length === 0 ? <EmptyState icon={CheckCircle2} title="All caught up!" sub="No pending tasks." /> : pending.map((t) => (
            <TaskRow key={t.id} t={t} onToggle={toggle} showStaff={isAdmin} />
          ))}
        </div>
      </div>

      {completed.length > 0 && (
        <div className="bg-white border rounded-xl shadow-sm">
          <div className="px-5 py-3.5 border-b"><h3 className="font-semibold font-display text-slate-900">Completed</h3></div>
          <div className="divide-y">{completed.map((t) => <TaskRow key={t.id} t={t} onToggle={toggle} showStaff={isAdmin} />)}</div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input data-testid="tf-title" value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Description</Label><Textarea data-testid="tf-desc" value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Related Order</Label>
                <Select value={f.order_id} onValueChange={(v) => set("order_id", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="tf-order"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{orders.map((o) => <SelectItem key={o.order_id} value={o.order_id}>{o.order_id} — {o.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Due Date</Label><Input data-testid="tf-due" type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" /></div>
            </div>
            {isAdmin && (
              <div><Label>Assign to Staff</Label>
                <Select value={f.assigned_staff_id} onValueChange={(v) => set("assigned_staff_id", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="tf-staff"><SelectValue placeholder="Staff member" /></SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-task" onClick={save} className="bg-[#1e3a8a]">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskRow({ t, onToggle, showStaff }) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5" data-testid={`task-${t.id}`}>
      <Checkbox checked={t.status === "completed"} onCheckedChange={() => onToggle(t)} data-testid={`task-check-${t.id}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${t.status === "completed" ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
          {t.order_id && <span className="font-mono">{t.order_id}</span>}
          {t.due_date && <span>Due {formatDate(t.due_date)}</span>}
          {showStaff && t.assigned_staff_name && <span>{t.assigned_staff_name}</span>}
        </div>
      </div>
      {t.due_date && isToday(t.due_date) && t.status === "pending" && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Today</span>}
    </div>
  );
}
